/*
 * ==============================
 * McpServerManageOverlay.jsx - Inline panel for editing or deleting MCP Servers.
 * Renders inside the NextMessageOptionsModal right panel.
 * ==============================
 */
import React from 'react';
import { motion } from 'framer-motion';
import { X, Trash2, Edit } from 'lucide-react';

const McpServerManageOverlay = ({ isOpen, onClose, servers, onEditServer, onDeleteServer, isProcessing }) => {
    if (!isOpen) return null;

    return (
        <motion.div 
            className="absolute inset-0 z-20 bg-surface-dark backdrop-blur-sm rounded-lg flex flex-col p-3 shadow-glass border border-[var(--border-color)]" 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: 10 }} 
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <h3 className="text-sm font-medium text-on-surface">Manage MCP Servers</h3>
                <button onClick={onClose} disabled={isProcessing} className="p-1 rounded-full hover:bg-surface-dark/50 text-on-surface-dim disabled:opacity-50">
                    <X size={16} />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1 pr-1">
                {servers.length === 0 ? (
                    <p className="text-xs text-on-surface-dim/70 py-2 text-center">No servers to manage.</p>
                ) : (
                    servers.map(server => (
                        <div key={server.id} className={`flex items-center justify-between p-2 rounded-md hover:bg-surface-dark/40 transition-colors ${!(server.enabled ?? true) ? 'opacity-60' : ''}`}>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-on-surface truncate">{server.name}</h4>
                                <p className="text-[10px] text-on-surface-dim/70 truncate">{server.description || server.url}</p>
                            </div>
                            <div className="flex items-center gap-1.5 ml-2">
                                <button
                                    onClick={() => onEditServer(server)}
                                    disabled={isProcessing}
                                    className="p-1 hover:bg-primary/20 rounded disabled:opacity-50 transition-colors"
                                    title={`Edit ${server.name}`}
                                >
                                    <Edit size={14} className="text-primary" />
                                </button>
                                <button
                                    onClick={() => onDeleteServer(server.id)}
                                    disabled={isProcessing}
                                    className="p-1 hover:bg-red-500/20 rounded disabled:opacity-50 transition-colors"
                                    title={`Delete ${server.name}`}
                                >
                                    <Trash2 size={14} className="text-red-400" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </motion.div>
    );
};

export default McpServerManageOverlay;